import React from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { PageHeader, Stat } from "@/components/app/PageHeader";
import { categoryLabel, missionLabel } from "@/lib/labels";
import { useAuth } from "@/lib/auth";
import { canAccess } from "@/lib/permissions";
import { Link } from "react-router-dom";

const fetcher = (url) => api.get(url).then((r) => r.data);

const DEADLINE_BADGE = {
  en_retard: { label: "En retard", bg: "#FEF2F2", fg: "#991B1B" },
  urgent: { label: "Urgent", bg: "#FFFBEB", fg: "#92400E" },
  a_venir: { label: "À venir", bg: "#F0EDF8", fg: "#2E3192" },
};

function UpcomingDeadlines() {
  const { user } = useAuth();
  const { data } = useSWR(canAccess(user?.role, "echeances") ? "/accounting/deadlines" : null, fetcher, { shouldRetryOnError: false });
  const items = (data?.items || []).slice(0, 6);
  if (!data || !items.length) return null;
  return (
    <div className="lg:col-span-3 border border-zinc-200 bg-white rounded-md p-6" data-testid="dashboard-deadlines">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Prochaines échéances fiscales</div>
        <Link to="/echeances" className="text-xs text-[#2E3192] hover:underline" data-testid="dashboard-deadlines-link">Tout voir</Link>
      </div>
      <ul className="divide-y divide-zinc-100">
        {items.map((d) => {
          const s = DEADLINE_BADGE[d.status] || DEADLINE_BADGE.a_venir;
          return (
            <li key={d.id} className="py-2.5 flex items-center gap-4">
              <span className="text-sm font-medium w-24 shrink-0">{d.due_date}</span>
              <span className="text-sm truncate flex-1">{d.label} — {d.client_name}</span>
              <span className="text-sm text-zinc-500 shrink-0">{d.amount != null ? `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(d.amount)} ${d.currency}` : ""}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0" style={{ background: s.bg, color: s.fg }}>{s.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data, isLoading } = useSWR("/dashboard/stats", fetcher);

  const stats = data || {};
  const myBy = stats.my_time_month?.by_category || {};
  const teamBy = stats.team_time_month?.by_category || {};

  return (
    <div>
      <PageHeader
        eyebrow="Vue d'ensemble"
        title={`Bonjour ${user?.full_name?.split(" ")[0] || ""}.`}
        description="Situation du cabinet, votre temps du mois et alertes de conformité."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat testid="stat-clients" label="Clients" value={isLoading ? "—" : stats.clients_count ?? 0} />
        <Stat testid="stat-missions" label="Missions actives" value={isLoading ? "—" : stats.active_missions ?? 0} hint={`${stats.missions_count ?? 0} au total`} />
        <Stat testid="stat-collab" label="Collaborateurs" value={isLoading ? "—" : stats.users_count ?? 0} />
        <Stat testid="stat-lcbft" label="LCB-FT risque élevé" value={isLoading ? "—" : stats.lcbft_high_risk ?? 0} hint="Vigilance renforcée requise" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <UpcomingDeadlines />
        <div className="lg:col-span-2 border border-zinc-200 bg-white rounded-md p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">Mon temps ce mois-ci</div>
          <div className="font-serif-display text-5xl">{(stats.my_time_month?.total ?? 0).toFixed(1)}<span className="text-2xl text-zinc-500 ml-2">h</span></div>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
            {["facturable", "administratif", "commercial", "formation", "absence"].map((c) => (
              <div key={c} className="border border-zinc-200 rounded p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{categoryLabel(c)}</div>
                <div className="font-serif-display text-2xl mt-1">{(myBy[c] ?? 0).toFixed(1)}</div>
              </div>
            ))}
          </div>
          {(user?.role === "admin" || user?.role === "expert") && (
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Équipe — temps ce mois</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {["facturable", "administratif", "commercial", "formation", "absence"].map((c) => (
                  <div key={c} className="border border-zinc-200 rounded p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{categoryLabel(c)}</div>
                    <div className="font-serif-display text-xl mt-1">{(teamBy[c] ?? 0).toFixed(1)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border border-zinc-200 bg-white rounded-md p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-4">Missions récentes</div>
          <ul className="divide-y divide-zinc-100">
            {(stats.recent_missions || []).map((m) => (
              <li key={m.id} className="py-3">
                <Link to="/missions" className="block">
                  <div className="text-sm font-medium truncate">{m.title}</div>
                  <div className="text-xs text-zinc-500">{missionLabel(m.mission_type)} • {m.status}</div>
                </Link>
              </li>
            ))}
            {!stats.recent_missions?.length && <li className="py-6 text-sm text-zinc-500">Aucune mission enregistrée.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
